import type {
  WidgetMetrics,
  WidgetData,
  WidgetConfig,
  IdentifierType,
  WidgetTheme,
} from '../types';
import { METRIC_COLORS } from './PlumPrint';
import PlumXLogo from './PlumXLogo';
import PlumPrint from './PlumPrint';
import { buildDetailsLink } from '@plumx/utils';
import { groupByLabel, toDetailsLinkOptions } from '../utils/stats';
import { isAlternateTheme } from '../utils/theme';
import { CATEGORIES, visibleCategories } from '../utils/categories';
import { useTranslation } from '../i18n/i18n';
import styles from '../styles/widget.module.scss';

interface DetailsProps {
  metrics: WidgetMetrics;
  data: WidgetData;
  config: WidgetConfig;
  printSize?: 'xs' | 'sm' | 'md' | 'lg';
  identifier: string;
  identifierType: IdentifierType;
  theme?: WidgetTheme;
}

const Details = ({
  metrics,
  data,
  config,
  printSize = 'lg',
  identifier,
  identifierType,
  theme,
}: DetailsProps) => {
  const { t } = useTranslation();
  const detailsLink = buildDetailsLink(identifier, identifierType, toDetailsLinkOptions(config));
  const hideHeader = isAlternateTheme(theme);

  return (
    <div
      className={`${styles.detailsCard}${config.showBorder !== false ? ` ${styles.detailsCardBordered}` : ''}`}
    >
      {!hideHeader && (
        <div className={styles.detailsHeader}>
          <PlumXLogo variant="white" size="md" />
        </div>
      )}
      <div className={styles.detailsMain}>
        {!config.hidePrint && (
          <div className={styles.detailsPrintArea}>
            <PlumPrint metrics={metrics} size={printSize} />
          </div>
        )}
        <div className={styles.detailsColumns}>
          {visibleCategories(CATEGORIES, metrics, config).map(
            ({ label, metricsKey, statsKey, aggregation, detailsMode }) => {
              const items = data.statistics[statsKey] ?? [];
              const color = METRIC_COLORS[metricsKey];
              const rows =
                detailsMode === 'aggregate'
                  ? groupByLabel(items, aggregation).map(({ label: statLabel, total }) => ({
                      key: statLabel,
                      node: (
                        <>
                          {t(statLabel)}: <strong>{total.toLocaleString()}</strong>
                        </>
                      ),
                    }))
                  : items.map((item, index) => ({
                      key: `${item.source}-${item.label}-${index}`,
                      node: (
                        <>
                          {t(item.source)} – {t(item.label)}:{' '}
                          <strong>{item.count.toLocaleString()}</strong>
                        </>
                      ),
                    }));
              return (
                <div key={metricsKey} className={styles.detailsColumn}>
                  <div className={styles.detailsColumnBar} style={{ backgroundColor: color }} />
                  <div className={styles.detailsColumnLabel} style={{ color }}>
                    {t(label)}
                  </div>
                  {rows.slice(0, 6).map(({ key, node }) => (
                    <div key={key} className={styles.detailsStatItem}>
                      {node}
                    </div>
                  ))}
                  {rows.length > 6 && <div className={styles.detailsStatItem}>…</div>}
                </div>
              );
            },
          )}
        </div>
      </div>
      {!config.disableLinkToPlumx && (
        <div className={styles.detailsFooter}>
          <PlumXLogo variant="color" size="sm" />
          <span className={styles.detailsFooterSep}>–</span>
          <a
            href={detailsLink}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.seeDetails}
          >
            {t('see details')}
            <svg
              className={styles.linkIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 128 128"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M18 109.855V18.14h36v10H28v71.715h72V74.14h9.996v35.715zM69.98 28.14v-10h40v40h-10V35.21l-35.8 35.82-7.08-7.08 35.81-35.81z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>
      )}
    </div>
  );
};

export default Details;
